import { differenceInYears, format, isValid, parse, parseISO } from 'date-fns';

export const formatDateString = (dateString: string): string | undefined => {
    try {
        const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
        if (!isValid(parsedDate)) {
            return undefined;
        }
        return formatSeriesDate(parsedDate);
    } catch (error) {
        return undefined;
    }
};

/**
 * A DICOM review date (DA, `YYYYMMDD`) and review time (TM, `HHMM...`) as
 * one display string: the date, then `HH:mm` when the time has at least
 * hours and minutes. `-` when the date is missing or not eight characters.
 */
export const formatReviewDateTime = (
    reviewDate?: string,
    reviewTime?: string
): string => {
    if (!reviewDate || reviewDate.length !== 8) {
        return '-';
    }

    const datePart = formatDateString(reviewDate) ?? '-';

    if (!reviewTime || reviewTime.length < 4) {
        return datePart;
    }

    const hours = reviewTime.slice(0, 2);
    const minutes = reviewTime.slice(2, 4);

    return `${datePart} ${hours}:${minutes}`;
};

export function formatDate(dateString: string): Date | undefined {
    try {
        const value = dateString?.trim();
        // DICOM DA is exactly YYYYMMDD. A shorter value still parses, into a real
        // but wrong date ('2026021' becomes 1 Feb 2026), so refuse it outright.
        if (!value || value.length !== 8) {
            return undefined;
        }
        const parsed = parse(value, 'yyyyMMdd', new Date());
        // An Invalid Date is truthy, so it survives every downstream falsy guard
        // and reaches date-fns format(), which throws RangeError.
        return isValid(parsed) ? parsed : undefined;
    } catch (error) {
        return undefined;
    }
}

/**
 * Elapsed time as `mm:ss`, or `HH:mm:ss` once past an hour.
 *
 * Integer arithmetic, not a Date: projecting an elapsed span onto a clock makes
 * the result depend on the host offset, and makes hours wrap at a full day.
 *
 * @param totalSeconds - Elapsed seconds; negative values read as zero.
 */
export function formatElapsed(totalSeconds: number): string {
    const whole = Number.isFinite(totalSeconds)
        ? Math.max(0, Math.floor(totalSeconds))
        : 0;
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const seconds = whole % 60;
    const pad = (value: number): string => String(value).padStart(2, '0');
    return hours > 0
        ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Formats a Date object for display (e.g., "Dec 3 2025").
 * @param date - The Date object to format.
 * @returns A formatted date string, or empty string if date is undefined.
 */
export const formatSeriesDate = (date: Date | undefined): string => {
    if (!date) {
        return '';
    }
    const locale = navigator.language;
    return format(date, locale === 'en-US' ? 'MMM d yyyy' : 'd MMM yyyy');
};

/**
 * Formats a DICOM date string (yyyyMMdd) for display (e.g., "Jan 1 1900").
 * @param dateString - The DICOM date string in yyyyMMdd format.
 * @returns A formatted date string, or undefined if parsing fails.
 */
export const formatDateCompact = (dateString: string): string | undefined => {
    const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
    if (!isValid(parsedDate)) {
        return undefined;
    }
    return formatSeriesDate(parsedDate);
};

/**
 * Calculates age in years from a DICOM date string (yyyyMMdd).
 * @param dateString - The DICOM date string in yyyyMMdd format.
 * @returns Age in years, or undefined if parsing fails or date is in the future.
 */
export const calculateAge = (dateString: string): number | undefined => {
    const parsedDate = parse(dateString, 'yyyyMMdd', new Date());
    if (!isValid(parsedDate)) {
        return undefined;
    }
    const age = differenceInYears(new Date(), parsedDate);
    if (age < 0) {
        return undefined;
    }
    return age;
};

/**
 * Wire date/timestamp -> Date, or undefined when missing or unparseable.
 *
 * An instant connector-next records (createdAt, lastAccessedAt) carries its
 * zone designator, so it reads as the viewer's own time. A value without one is
 * read as the wall clock it was written in. parseISO is what makes that
 * uniform: the native Date constructor reads a date-only value as UTC but a
 * zoneless timestamp as local, which moves birthDate a day west of Greenwich.
 *
 * That reading is exactly right for the DICOM values (seriesDateTime and
 * birthDate name a day, not an instant). lastActivityAt arrives without a
 * zone, so its UTC clock reading is shown as-is.
 */
export function parseWireDate(
    value: string | null | undefined
): Date | undefined {
    if (!value) {
        return undefined;
    }
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : undefined;
}
